import { useAnchoredPanel } from "@hooks/useAnchoredPanel"
import { cn } from "@lib/cn"
import { Bell } from "lucide-react"
import { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"

import { useUnreadCount } from "../hooks"

import {
  NOTIFICATION_PANEL_MAX_HEIGHT,
  NOTIFICATION_PANEL_WIDTH,
  NotificationDropdown,
} from "./NotificationDropdown"

interface Props {
  className?: string
}

/**
 * PRD-26 §FR-10 — the global bell affordance in the sidebar header.
 * Shows a tone-shifted dot when the unread count > 0; "9+" caps at
 * double digits to keep the badge compact.
 *
 * The popover portals to document.body via `useAnchoredPanel`, which owns
 * positioning, the outside-pointerdown dismissal, Escape, the flip-up when the
 * bell sits low in the viewport, and the height clamp that keeps a long
 * notification list reachable.
 */
export function NotificationBell({ className }: Props) {
  const { t } = useTranslation("notifications")
  const [open, setOpen] = useState(false)
  const { data: unread = 0 } = useUnreadCount()

  const close = useCallback(() => setOpen(false), [])
  const {
    triggerRef: buttonRef,
    panelRef: popoverRef,
    panelStyle,
  } = useAnchoredPanel({
    open,
    onClose: close,
    width: NOTIFICATION_PANEL_WIDTH,
    maxHeight: NOTIFICATION_PANEL_MAX_HEIGHT,
    gap: 8,
  })

  const hasUnread = unread > 0
  const badge = unread > 9 ? "9+" : String(unread)

  return (
    <div className={cn("inline-flex", className)}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={hasUnread ? t("bell.aria_unread", { count: unread }) : t("bell.aria")}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="rounded-control border-border text-ink-muted hover:bg-inset hover:text-ink relative inline-flex h-11 w-11 items-center justify-center border transition-colors duration-150 md:h-9 md:w-9"
        title={hasUnread ? t("bell.aria_unread", { count: unread }) : t("bell.aria")}
      >
        <Bell className="h-4 w-4" aria-hidden />
        {hasUnread ? (
          <span
            aria-hidden
            className="bg-accent text-ink-inverted absolute top-1 right-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-xs font-semibold md:-top-1 md:-right-1"
          >
            {badge}
          </span>
        ) : null}
      </button>

      {open && panelStyle ? (
        <NotificationDropdown style={panelStyle} popoverRef={popoverRef} onClose={close} />
      ) : null}
    </div>
  )
}
